import styles from "./Memos.module.scss";
import { Content, ContentFixed, ContentScroll } from "../../common/Content";
import { HeaderMenu } from "../../common/HeaderMenu";
import React, { useEffect, useRef, useState } from "react";
import { FabButton } from "../../common/FabButton";
import { MODE_CREATE, MODE_EDIT } from "../../codes";
import Card from "../../common/Card";
import { Link, useParams, useSearchParams } from "react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiCreateMemoFile, apiDeleteMemo, apiDeleteMemoFile, apiGetMemos } from "../../api/ApiMemos";
import { apiDeleteFolder, apiGetFolders } from "../../api/ApiFolders";
import { AppModal, DeleteModal } from "../../common/AppModal";
import FolderEditor from "./FolderEditor";
import { ItemActions } from "../../common/ItemActions";
import MemoEditor from "./MemoEditor";
import useToast from "../../util/useToast";
import { useDebounce } from "use-debounce";
import Form from "react-bootstrap/esm/Form";
import { AppPagination } from "../../common/AppPagination";
import clsx from "clsx";
import { useFileUploader } from "../../util/useFileUploader";
import { PhotoViewer } from "../../common/PhotoViewer";
import { renderDescription } from "../../util/util";

const Memos = () => {
  const params = useParams();

  const { showToastSuccess, showToastError } = useToast();

  const [searchParams, setSearchParams] = useSearchParams();

  const [searchInputValue, setSearchInputValue] = useState(searchParams.get("filter") || "");
  const [debouncedFilter] = useDebounce(searchInputValue, 200);

  const [folderEditor, setFolderEditor] = useState({ show: false, mode: MODE_CREATE, folder: {} });
  const [memoEditor, setMemoEditor] = useState({ show: false, mode: MODE_CREATE, memo: {} });
  const [deleteFolderModal, setDeleteFolderModal] = useState({ show: false, folder: null });
  const [deleteMemoModal, setDeleteMemoModal] = useState({ show: false, memo: null });
  const [deleteFileModal, setDeleteFileModal] = useState({ show: false, memo: null, file: null });
  const [fileMemo, setFileMemo] = useState(null);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [photosPerRow, setPhotosPerRow] = useState(6);
  const [displayedPhotoMemoId, setDisplayedPhotoMemoId] = useState(null);
  const [displayedPhoto, setDisplayedPhoto] = useState(null);
  const [photos, setPhotos] = useState([]);

  const fileInputRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;

      if (width < 581) {
        setPhotosPerRow(3);
      } else if (width < 721) {
        setPhotosPerRow(4);
      } else if (width < 861) {
        setPhotosPerRow(5);
      } else {
        setPhotosPerRow(6);
      }
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setSearchInputValue(searchParams.get("filter") || "");
  }, [params.folderId]);

  useEffect(() => {
    const filter = debouncedFilter.trim();

    if (filter !== (searchParams.get("filter") || "")) {
      setSearchParams(filter ? { filter } : {});
    }
  }, [debouncedFilter]);

  const queryParams = { size: 50, page: +searchParams.get("page") || 0 };

  if (searchParams.get("filter")) {
    queryParams.filter = searchParams.get("filter");
  }

  if (params.folderId) {
    queryParams.folderId = params.folderId;
  }

  const foldersQuery = useQuery({ ...apiGetFolders(), enabled: false });
  const memosQuery = useQuery({ ...apiGetMemos(queryParams), enabled: false });

  const deleteFolderMutation = useMutation(apiDeleteFolder());
  const deleteMemoMutation = useMutation(apiDeleteMemo());
  const createFileMutation = useMutation(apiCreateMemoFile());
  const deleteFileMutation = useMutation(apiDeleteMemoFile());

  const { uploadFiles, progress, total } = useFileUploader(
    (file, content) => createFileMutation.mutateAsync({ memoId: fileMemo.id, data: { name: file.name, content } })
  );

  const handleFileChange = async (event) => {
    setUploadingFiles(true);

    uploadFiles(event.target.files)
      .then(() => memosQuery.refetch())
      .finally(() => {
        event.target.value = "";

        setUploadingFiles(false);
      });
  };

  const deleteFile = () => {
    return deleteFileMutation.mutateAsync({ memoId: deleteFileModal.memo.id, fileId: deleteFileModal.file.id })
      .then(() => memosQuery.refetch())
      .then(() => {
        setDeleteFileModal({ show: false });

        showToastSuccess("Fichier supprimé");
      });
  };

  const memos = memosQuery.data.content;
  const totalPages = memosQuery.data.page.totalPages;
  const totalElements = memosQuery.data.page.totalElements;

  const folder = params.folderId ? foldersQuery.data.find(f => f.id === params.folderId) : null;

  const folders = foldersQuery.data
    .filter(f => {
      return (params.folderId && f.parentId === params.folderId) || (!params.folderId && !f.parentId);
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  const actions = [
    {
      icon: "folder",
      label: "Ajouter un dossier",
      onClick: () => setFolderEditor({ show: true, mode: MODE_CREATE, folder: {} })
    },
    {
      icon: "add",
      label: "Ajouter un mémo",
      onClick: () => setMemoEditor({ show: true, mode: MODE_CREATE, memo: { folderId: params.folderId } })
    },
  ];

  const confirmDeleteFile = (memo, file) => e => {
    e.preventDefault();

    setDeleteFileModal({ show: true, file, memo });
  };

  return <Content>
    <ContentFixed>
      <HeaderMenu title={"Mémos techniques"} />
    </ContentFixed>

    <ContentScroll paddingTop withFab>
      <div className={styles.memosLayout}>
        <div className={styles.searchBar}>
          <Form.Control
            type="text"
            className={styles.searchInput}
            placeholder="Chercher"
            value={searchInputValue}
            onChange={e => setSearchInputValue(e.target.value)}
          />
        </div>

        {(!!params.folderId || folders.length > 0) && <Card>
          {folder && <FolderUp parentFolderId={folder ? folder.parentId : null} />}

          {folders.map(folder => <Folder
            folder={folder}
            key={folder.id}
            onRename={() => setFolderEditor(() => ({ show: true, mode: MODE_EDIT, folder }))}
            onDelete={() => setDeleteFolderModal({ show: true, folder })}
          />)}
        </Card>}

        <Card>
          {memos.length === 0 && <div className={styles.memosEmpty}>Pas de mémos</div>}

          {memos.map(memo => {
            const memoActions = [
              /*{type: "item", icon: "folder", label: "Déplacer", onClick: console.log},*/
              {
                type: "item",
                icon: "edit",
                label: "Modifier",
                onClick: () => setMemoEditor(() => ({ show: true, mode: MODE_EDIT, memo }))
              },
              {
                type: "item",
                icon: "upload_file",
                label: "Ajouter des fichiers",
                onClick: () => {
                  setFileMemo(memo);

                  fileInputRef.current.click();
                }
              },
              { type: "divider" },
              {
                type: "item",
                icon: "delete",
                label: "Supprimer",
                onClick: () => setDeleteMemoModal({ show: true, memo })
              },
            ];

            const files = memo.files.filter(f => !f.pictureWidth);
            const photos = memo.files.filter(f => !!f.pictureWidth);

            const remainder = photos.length % photosPerRow;
            const spacersCount = remainder === 0 ? 0 : photosPerRow - remainder;

            return <div key={memo.id} className={styles.memo}>
              <div className={styles.view}>
                <div className={styles.name}>{memo.name}</div>
                {memo.description && <div className={styles.description}
                                          dangerouslySetInnerHTML={{ __html: renderDescription(memo.description) }} />}

                {(files.length > 0) && <div className={styles.files}>
                  {files.sort((a, b) => a.name.localeCompare(b.name)).map(file => {
                    const extension = file.name.split(".").pop()?.toLowerCase() || "unknown";

                    return <>
                      <a key={file.id}
                         className={styles.file}
                         href={process.env.REACT_APP_API_URL + "/public/mef/" + file.fileKey}
                         download={file.name}
                         title={file.name}
                         target="_blank">
                        <span className={clsx(styles.extension, styles[extension])}>{extension}</span>
                        <span className={styles.name}>{file.name}</span>
                        <span className={styles.delete} onClick={confirmDeleteFile(memo, file)}>
                          <span className={clsx("material-icons", styles.deleteIcon)}>close</span>
                        </span>
                      </a>
                    </>;
                  })}
                </div>}

                {(photos.length > 0) && <div className={styles.photos}>
                  {photos.sort((a, b) => a.name.localeCompare(b.name)).map(photo => {
                    return <span className={styles.photo} key={photo.id} onClick={() => {
                      setPhotos(photos);
                      setDisplayedPhoto(photo);
                      setDisplayedPhotoMemoId(memo.id);
                    }}>
                      <img src={process.env.REACT_APP_API_URL + "/public/mep/" + photo.fileKey + "?w=200&h=200"} />
                    </span>;
                  })}

                  {Array
                    .from({ length: spacersCount }, (_, x) => x)
                    .map(d => <div key={d} className={styles.photo}></div>)}
                </div>}
              </div>

              <ItemActions actions={memoActions} />
            </div>;
          })}
        </Card>

        <Card padding>
          <AppPagination
            itemName="mémo"
            page={queryParams.page}
            totalElements={totalElements}
            totalPages={totalPages}
          />
        </Card>
      </div>

      <FabButton icon="more_vert" actions={actions} />

      <FolderEditor
        show={folderEditor.show}
        mode={folderEditor.mode}
        hide={() => setFolderEditor(prev => ({ ...prev, show: false }))}
        folder={folderEditor.folder}
      />

      <MemoEditor
        show={memoEditor.show}
        mode={memoEditor.mode}
        hide={() => setMemoEditor(prev => ({ ...prev, show: false }))}
        memo={memoEditor.memo}
        onSubmit={() => memosQuery.refetch()}
      />

      <PhotoViewer
        photos={photos}
        photo={displayedPhoto}
        setPhoto={setDisplayedPhoto}
        reloadPhotos={() => memosQuery.refetch()}
        onDelete={photoId => deleteFileMutation.mutateAsync({ memoId: displayedPhotoMemoId, fileId: photoId })}
        publicPath="mep"
      />

      <DeleteModal
        show={deleteFolderModal.show}
        title="Supprimer le dossier"
        onCancel={() => setDeleteFolderModal({ show: false })}
        onSubmit={() => {
          return deleteFolderMutation.mutateAsync({ id: deleteFolderModal.folder.id })
            .then(() => {
              foldersQuery.refetch().then(() => {
                showToastSuccess("Dossier supprimé");

                setDeleteFolderModal(prev => ({ ...prev, show: false }));
              });
            })
            .catch(e => {
              if (e.response?.status === 400 && e.response?.data?.message === "folder_not_empty") {
                showToastError("Ce dossier n'est pas vide, impossible de le supprimer");

                setDeleteFolderModal(prev => ({ ...prev, show: false }));

                return;
              }

              throw e;
            });
        }}
        cancelLabel="Annuler"
        submitLabel="Supprimer"
      >
        Etes-vous sûr de vouloir supprimer ce dossier ?
      </DeleteModal>

      <DeleteModal
        show={deleteMemoModal.show}
        title="Supprimer le mémo"
        onCancel={() => setDeleteMemoModal({ show: false })}
        onSubmit={() => {
          return deleteMemoMutation.mutateAsync({ id: deleteMemoModal.memo.id }).then(() => {
            memosQuery.refetch().then(() => {
              showToastSuccess("Mémo supprimé");

              setDeleteMemoModal(prev => ({ ...prev, show: false }));
            });
          });
        }}
        cancelLabel="Annuler"
        submitLabel="Supprimer"
      >
        Etes-vous sûr de vouloir supprimer ce mémo ?
      </DeleteModal>

      <DeleteModal
        className={styles.deleteFileModal}
        size={"sm"}
        show={deleteFileModal.show}
        title="Supprimer le fichier"
        onCancel={() => setDeleteFileModal({ show: false })}
        onSubmit={deleteFile}
        cancelLabel="Annuler"
        submitLabel="Supprimer"
      >
        Etes-vous sûr de vouloir supprimer ce fichier ?
      </DeleteModal>

      <AppModal
        size="sm"
        show={uploadingFiles}
        title="Envoi en cours"
      >
        <p>Veuillez patienter pendant l'envoi de vos fichiers ({progress} / {total}).</p>

        <div className={styles.fileProgressBar}>
          <div className={styles.fileProgressBarValue}></div>
        </div>
      </AppModal>

      <input
        className={styles.inputFile}
        type="file"
        ref={fileInputRef}
        name="files"
        multiple="multiple"
        onChange={handleFileChange}
      />
    </ContentScroll>
  </Content>;
};

const FolderUp = ({ parentFolderId }) => {
  return <div className={styles.folder}>
    <Link to={parentFolderId ? "/memos/f/" + parentFolderId : "/memos"} className={styles.view}>
      <div className={styles.icon}>
        <span className="material-icons">arrow_upward</span>
      </div>
      <div className={styles.name}>...</div>
    </Link>
  </div>;
};

const Folder = ({ folder, onRename, onDelete }) => {
  const actions = [
    { type: "item", icon: "edit", label: "Renommer", onClick: onRename },
    { type: "item", icon: "folder", label: "Déplacer", onClick: console.log },
    { type: "divider" },
    { type: "item", icon: "delete", label: "Supprimer", onClick: onDelete },
  ];

  return <div className={styles.folder}>
    <Link to={"/memos/f/" + folder.id} className={styles.view}>
      <div className={styles.icon}>
        <span className="material-icons">folder</span>
      </div>
      <div className={styles.name}>{folder.name}</div>
    </Link>

    <ItemActions actions={actions} />
  </div>;
};

export default Memos;